import { useCallback, useEffect, useMemo, useState } from "react";

import { interpolateByRange } from "../../utils/interpolator";
import { usePointerMove } from "../../hooks/usePointerMove";
import { Handler } from "../../types/handler";
import { Range } from "../../types/range";
import { useSeekBar } from "../SeekBar";
import { useRange } from "../Range";
import { useRoot } from "../Root";
import { ClipContext } from ".";
import { useLayout } from "../Layout";

interface Props {
  children: React.ReactNode;
}

export const ClipProvider = ({ children }: Props) => {
  const { adjustedDuration, currentTimeOffset } = useRange();

  const { clipRange, setClipRange, clippingEnabled, minimumClipGap } =
    useRoot();

  const { isMobile } = useLayout();

  const {
    offset: seekBarOffset,
    width: seekBarWidth,
    lastPointerPosition,
    activeHandler,
  } = useSeekBar();

  const show = clippingEnabled && !isMobile;
  const isMovable = setClipRange !== undefined && !isMobile;

  const defaultStartTime = 0.25 * adjustedDuration;
  const defaultEndTime = 0.75 * adjustedDuration;

  const [startClipTime, setStartClipTime] = useState<number>();
  const [endClipTime, setEndClipTime] = useState<number>();

  useEffect(() => {
    if (show) {
      setStartClipTime(clipRange?.start ?? defaultStartTime);
      setEndClipTime(clipRange?.end ?? defaultEndTime);
    }
  }, [clipRange, defaultEndTime, defaultStartTime, show]);

  const clipRangeInPixels = useMemo(
    (): Range => ({
      start:
        (((startClipTime ?? defaultStartTime) - currentTimeOffset) /
          adjustedDuration) *
        seekBarWidth,
      end:
        (((endClipTime ?? defaultEndTime) - currentTimeOffset) /
          adjustedDuration) *
        seekBarWidth,
    }),
    [
      adjustedDuration,
      currentTimeOffset,
      defaultEndTime,
      defaultStartTime,
      endClipTime,
      seekBarWidth,
      startClipTime,
    ]
  );

  const clipRangeInTime = useMemo(
    (): Range => ({
      start: startClipTime ?? defaultStartTime,
      end: endClipTime ?? defaultEndTime,
    }),
    [defaultEndTime, defaultStartTime, endClipTime, startClipTime]
  );

  const moveStartHandler = useCallback(
    async (offset: number) => {
      const { end } = clipRange!;

      const realTime =
        adjustedDuration * (offset / seekBarWidth) + currentTimeOffset;

      const max = Math.min(
        end - minimumClipGap,
        adjustedDuration + currentTimeOffset
      );

      const newTime = interpolateByRange(currentTimeOffset, realTime, max);

      setStartClipTime(newTime);
    },

    [
      adjustedDuration,
      clipRange,
      currentTimeOffset,
      minimumClipGap,
      seekBarWidth,
    ]
  );

  const moveEndHandler = useCallback(
    async (offset: number) => {
      const { start } = clipRange!;

      const realTime =
        (offset / seekBarWidth) * adjustedDuration + currentTimeOffset;

      const min = Math.max(0, start + minimumClipGap);
      const newTime = interpolateByRange(
        min,
        realTime,
        adjustedDuration + currentTimeOffset
      );

      setEndClipTime(newTime);
    },
    [
      adjustedDuration,
      clipRange,
      currentTimeOffset,
      minimumClipGap,
      seekBarWidth,
    ]
  );

  const moveStartPointerHandler = useCallback(
    (e: PointerEvent) => {
      const offset = e.x - seekBarOffset;
      moveStartHandler(offset);
    },
    [moveStartHandler, seekBarOffset]
  );
  usePointerMove(
    isMovable && activeHandler === Handler.startClip,
    moveStartPointerHandler
  );

  const moveEndPointerHandler = useCallback(
    (e: PointerEvent) => {
      const offset = e.x - seekBarOffset;
      moveEndHandler(offset);
    },
    [moveEndHandler, seekBarOffset]
  );
  usePointerMove(
    isMovable && activeHandler === Handler.endClip,
    moveEndPointerHandler
  );

  useEffect(() => {
    if (
      activeHandler === Handler.startClip &&
      lastPointerPosition !== undefined
    ) {
      moveStartHandler(lastPointerPosition);
    }
  }, [activeHandler, lastPointerPosition, moveStartHandler]);

  useEffect(() => {
    if (
      activeHandler === Handler.endClip &&
      lastPointerPosition !== undefined
    ) {
      moveEndHandler(lastPointerPosition);
    }
  }, [activeHandler, lastPointerPosition, moveEndHandler]);

  useEffect(() => {
    if (
      activeHandler === undefined &&
      isMovable &&
      startClipTime !== undefined &&
      endClipTime !== undefined
    ) {
      setClipRange({
        start: +startClipTime.toFixed(4),
        end: +endClipTime.toFixed(4),
      });
    }
  }, [activeHandler, endClipTime, isMovable, setClipRange, startClipTime]);

  const value = useMemo(
    () => ({
      show,
      rangeInPixels: clipRangeInPixels,
      rangeInTime: clipRangeInTime,
      activeHandler,
    }),
    [activeHandler, clipRangeInPixels, clipRangeInTime, show]
  );

  return <ClipContext.Provider value={value}>{children}</ClipContext.Provider>;
};
